import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError, toApiError } from '@/lib/api'
import type { UserRoleResponse } from '@/lib/api'
import { useAnswerApi } from '@/hooks/useAnswerApi'

export type UserRolesState =
  | { kind: 'loading' }
  | { kind: 'ready'; roles: UserRoleResponse[] }
  | { kind: 'error'; message: string }

export type UseUserRolesOptions = {
  baseUrl: string
  acquireToken: () => Promise<string>
  fetchImpl?: typeof fetch
  /** Gate the load on the caller being a confirmed admin — the list endpoint
   * is admin-only and would 403 otherwise. */
  enabled?: boolean
}

export type UseUserRolesResult = {
  state: UserRolesState
  /** Last grant-by-email failure (e.g. unknown email → 404, directory down →
   * 503), or null. Cleared when a grant succeeds or a new one starts. */
  grantError: string | null
  refresh: () => Promise<void>
  grantAdmin: (email: string) => Promise<void>
  revokeAdmin: (oid: string) => Promise<void>
}

export function useUserRoles(options: UseUserRolesOptions): UseUserRolesResult {
  const enabled = options.enabled ?? true
  const [state, setState] = useState<UserRolesState>({ kind: 'loading' })
  const [grantError, setGrantError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const api = useAnswerApi(options)

  const refresh = useCallback(async () => {
    const controller = new AbortController()
    abortRef.current?.abort()
    abortRef.current = controller
    try {
      const roles = await api.listRoles({ signal: controller.signal })
      if (controller.signal.aborted) return
      setState({ kind: 'ready', roles })
    } catch (err) {
      if (controller.signal.aborted) return
      const apiErr: ApiError = err instanceof ApiError ? err : await toApiError(err)
      setState((prev) => (prev.kind === 'ready' ? prev : { kind: 'error', message: apiErr.message }))
    } finally {
      if (abortRef.current === controller) abortRef.current = null
    }
  }, [api])

  const grantAdmin = useCallback(
    async (email: string) => {
      const trimmed = email.trim()
      if (trimmed === '') return
      setGrantError(null)
      try {
        await api.grantAdmin({ grantAdminRequest: { email: trimmed } })
      } catch (err) {
        const apiErr: ApiError = err instanceof ApiError ? err : await toApiError(err)
        setGrantError(
          apiErr.status === 404
            ? `Fant ingen bruker med e-post ${trimmed}.`
            : `Kunne ikke gi tilgang: ${apiErr.message}`,
        )
        return
      }
      await refresh()
    },
    [api, refresh],
  )

  const revokeAdmin = useCallback(
    async (oid: string) => {
      await api.revokeAdmin({ oid })
      await refresh()
    },
    [api, refresh],
  )

  const refreshRef = useRef(refresh)
  useEffect(() => {
    refreshRef.current = refresh
  })
  useEffect(() => {
    if (!enabled) return
    setState((prev) => (prev.kind === 'ready' ? prev : { kind: 'loading' }))
    void refreshRef.current()
    return () => {
      abortRef.current?.abort()
    }
  }, [enabled])

  return { state, grantError, refresh, grantAdmin, revokeAdmin }
}
